import { motion } from 'framer-motion';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const DepreciationPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-block mb-6 px-5 py-2 border border-red/20 bg-red/5 rounded-full">
                <span className="text-red text-sm font-medium">税務</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                減価償却
              </h1>
            </motion.div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-lg max-w-none"
            >
              <div className="space-y-8 text-gray-700 leading-relaxed text-[17px]">
                <p>
                  会計には減価償却というものがあります。
                </p>

                <div className="bg-gradient-to-br from-red/5 to-red-light/5 rounded-2xl p-8 border border-red/10 my-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">減価償却とは</h2>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-car-line text-red text-xl"></i>
                    </div>
                    <div>
                      <p className="text-lg mb-4">
                        例えば、これは車を買ったら普通、一年でのりつぶすことはなく、何年かにわたってつかうと思います。
                      </p>
                      <p className="text-xl font-bold text-gray-900">
                        つまり車は何年かにわたっていたんでいきます。
                      </p>
                      <p className="text-gray-600 mt-3">
                        そのことを数字でしめすための方法です。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 border-l-4 border-red shadow-sm my-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">具体的な計算例</h2>
                  
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-red/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-calculator-line text-red text-xl"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">定額法による減価償却</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-5 border border-red/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-600">車の購入価格</span>
                          <span className="text-2xl font-bold text-gray-900">1,000,000円</span>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-5 border border-red/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-600">使用期間（耐用年数）</span>
                          <span className="text-2xl font-bold text-gray-900">5年</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-center py-3">
                        <i className="ri-arrow-down-line text-red text-3xl"></i>
                      </div>
                      
                      <div className="bg-red text-white rounded-lg p-6">
                        <div className="text-center">
                          <p className="text-sm mb-2 opacity-90">毎年の減価償却費</p>
                          <p className="text-4xl font-bold">200,000円</p>
                          <p className="text-sm mt-2 opacity-90">（1,000,000円 ÷ 5年）</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600">
                    1,000,000円の車を買って1年で車はつぶれないので、だいたい5年使うとすると毎年200,000円ずついたんでいく（定額法）といった考えかたをします。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-red/5 to-red-light/5 rounded-2xl p-8 border border-red/10 my-10">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-red rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">重要な注意点</h2>
                      <p className="text-lg font-medium text-red">
                        お金の流れと利益がずれる
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-red/20">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">現金で買った場合</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-red/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-red font-bold">1</span>
                        </div>
                        <div className="flex-1">
                          <strong className="text-gray-900 block mb-2">最初に出ていくお金</strong>
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <p className="text-2xl font-bold text-gray-900">1,000,000円</p>
                            <p className="text-sm text-gray-600 mt-1">購入時に一括で支払い</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-red/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-red font-bold">2</span>
                        </div>
                        <div className="flex-1">
                          <strong className="text-gray-900 block mb-2">毎年いたんでいく金額</strong>
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <p className="text-2xl font-bold text-gray-900">200,000円/年</p>
                            <p className="text-sm text-gray-600 mt-1">5年間にわたって計上</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 bg-red/5 rounded-lg p-5 border border-red/20">
                      <div className="flex items-start gap-3">
                        <i className="ri-error-warning-line text-red text-2xl flex-shrink-0"></i>
                        <div>
                          <p className="font-bold text-gray-900 mb-2">
                            もうかっているのに預金通帳にはお金がない
                          </p>
                          <p className="text-gray-600 text-[15px]">
                            このような状況になりやすいので注意してください。
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm my-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">減価償却の理解ポイント</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-red"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900">資産の価値減少を表す</strong>
                        <p className="text-gray-600 text-sm mt-1">使用により徐々に価値が下がる</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-red"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900">費用を分散させる</strong>
                        <p className="text-gray-600 text-sm mt-1">使用期間に応じて費用計上</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-red"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900">現金支出と費用のズレ</strong>
                        <p className="text-gray-600 text-sm mt-1">購入時と費用計上時期が異なる</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-red"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900">資金繰りへの影響</strong>
                        <p className="text-gray-600 text-sm mt-1">利益と現金残高の違いに注意</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-8 mt-12">
                  <p className="text-gray-600">
                    減価償却は会計の基本的な考え方ですが、現金の流れと利益のズレを生み出すため、経営者は特に注意が必要です。帳簿上は利益が出ているのに、手元に現金がないという状況を避けるため、資金繰りの計画をしっかり立てることが重要です。
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </article>

        {/* Related Articles */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">関連記事</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <a
                href="/blog/company-history-profile"
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE('/blog/company-history-profile');
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/078a2e67b8da06ee706de50aa4613620.jpg"
                    alt="会社の沿革"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="inline-block mb-3 bg-red text-white px-3 py-1 rounded-full text-xs font-medium">
                    会社の沿革
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-red transition-colors">
                    会社の沿革、経営者のプロフィールは重要
                  </h3>
                </div>
              </a>

              <a
                href="/blog/business-planning"
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE('/blog/business-planning');
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=business%20planning%20documents%20with%20financial%20charts%20and%20strategic%20roadmap%20on%20clean%20desk%2C%20professional%20workspace%20with%20laptop%20and%20planning%20materials%2C%20organized%20business%20setting%20with%20natural%20lighting%2C%20corporate%20photography%20style%20emphasizing%20strategic%20thinking&width=800&height=500&seq=blog-002&orientation=landscape"
                    alt="事業計画"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="inline-block mb-3 bg-red text-white px-3 py-1 rounded-full text-xs font-medium">
                    事業計画
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-red transition-colors">
                    事業計画をつくるのはこんな感じです
                  </h3>
                </div>
              </a>

              <a
                href="/blog/multiple-income"
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE('/blog/multiple-income');
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=multiple%20income%20streams%20concept%20with%20financial%20documents%20and%20calculator%2C%20organized%20tax%20preparation%20materials%20on%20modern%20desk%2C%20professional%20accounting%20workspace%20with%20clean%20aesthetic%2C%20business%20photography%20emphasizing%20financial%20management&width=800&height=500&seq=blog-003&orientation=landscape"
                    alt="確定申告"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="inline-block mb-3 bg-red text-white px-3 py-1 rounded-full text-xs font-medium">
                    確定申告
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-red transition-colors">
                    複数の収入をもつ
                  </h3>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-red-dark to-red">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                減価償却のお悩み、お気軽にご相談ください
              </h2>
              <p className="text-white/90 mb-10 text-lg leading-relaxed">
                会計・税務の専門家として、あなたのビジネスをサポートします。<br />
                まずは無料相談で、お気軽にご相談ください。
              </p>
              <a
                href="/#contact"
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className="inline-block bg-white text-red px-12 py-4 rounded-full text-[15px] font-medium hover:bg-gray-50 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-xl"
              >
                無料相談を予約する
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DepreciationPage;